'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import { Alert, AlertTitle, Snackbar, Stack } from '@mui/material'

import { setGlobalToastInstance } from '@/lib/toast'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastMessage {
  id: string
  type: ToastType
  message: string
  title?: string
  duration?: number
  persistent?: boolean
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, options?: Partial<ToastMessage>) => void
  hideToast: (id: string) => void
  clearAll: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: React.ReactNode
}

const TOAST_ICONS = {
  success: CheckCircleIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
} as const

const TOAST_DURATIONS = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 4000,
} as const

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }, [])

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', options?: Partial<ToastMessage>) => {
      const id = generateId()
      const duration = options?.duration ?? TOAST_DURATIONS[type]

      const newToast: ToastMessage = {
        id,
        type,
        message,
        duration,
        persistent: options?.persistent ?? false,
        ...(options?.title && { title: options.title }),
        ...options,
      }

      setToasts((prev) => [...prev, newToast])

      // Auto-hide toast if not persistent
      if (!newToast.persistent && duration > 0) {
        setTimeout(() => {
          hideToast(id)
        }, duration)
      }
    },
    [generateId, hideToast]
  )

  const clearAll = useCallback(() => {
    setToasts([])
  }, [])

  const contextValue = useMemo(
    () => ({
      showToast,
      hideToast,
      clearAll,
    }),
    [showToast, hideToast, clearAll]
  )

  // Initialize global toast instance for compatibility with existing code
  useEffect(() => {
    setGlobalToastInstance({
      showToast,
      hideToast,
      clearAll,
    })
  }, [showToast, hideToast, clearAll])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Stack
        spacing={1}
        sx={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 9999,
          maxWidth: 400,
        }}
      >
        {toasts.map((toast) => {
          const IconComponent = TOAST_ICONS[toast.type]

          return (
            <Snackbar
              key={toast.id}
              open={true}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{ position: 'relative' }}
            >
              <Alert
                severity={toast.type}
                onClose={() => hideToast(toast.id)}
                icon={<IconComponent />}
                sx={{
                  minWidth: 300,
                  boxShadow: 3,
                }}
              >
                {toast.title && <AlertTitle>{toast.title}</AlertTitle>}
                {toast.message}
              </Alert>
            </Snackbar>
          )
        })}
      </Stack>
    </ToastContext.Provider>
  )
}
